import React, { useEffect, useState } from 'react';
import { useRecommendationsContext } from 'src/context/RecommendationsProvider';
import { useToast } from 'src/context/ToastProvider';
import { inviteCollaboratorForPosition } from 'src/apis/position';
import { useMutation, useQueryClient } from 'react-query';
import { get } from 'lodash';
import { Drawer } from '../shared/Drawer';
import { useMediaQuery } from '@mui/material';
import { Button, Label, Spinner, TextInput, ToggleSwitch } from 'flowbite-react';
import { validateEmailAddress, validateName, validatePhoneNumber } from 'shared-utils';
import HRSelection from './HRSelection';
import { useGetAllRecruitersOfCompany } from 'src/hooks/company.hooks';
import { useParams } from 'react-router-dom';
import { InviteCollaboratorForPositionInput, UIUser } from 'types';
import { useUserContext } from 'src/context/UserProvider';

const textInputStyle = {
	color: 'black',
	backgroundColor: 'white',
	border: '1px solid #D1D5DB',
};
const labelStyle = `dark:text-white text-[#111928]`;

const InviteCollaborator = ({
	isOpen,
	onClose,
	positionId,
	fromDriveFlow,
}: {
	isOpen: boolean;
	positionId?: string;
	onClose: () => void;
	fromDriveFlow?: boolean;
}) => {
	const toast = useToast();
	const { id } = useParams();
	const { companyId } = useParams();
	const { isAccountManagerLoggedIn } = useUserContext();
	const queryClient = useQueryClient();
	const { user } = useUserContext();
	const isMobile = useMediaQuery('(max-width:960px)');
	const [existingRec, setExistingRec] = useState<UIUser | undefined>();
	const { selectedCompany } = useRecommendationsContext();
	const [newRecruiterData, setNewRecruiterData] = useState<InviteCollaboratorForPositionInput>({
		name: '',
		email: '',
		phone: '',
		companyId: companyId || id || user?.registeredCompany?.toString() || '',
		positionId: positionId || '',
		hideRecommendations: false,
		recruiterDesignation: '',
	});
	const [disableNewRecruiterFields, setDisableNewRecruiterFields] = useState<boolean>(false);

	const { data: recruitersData, refetch: refetchCompanyRecruiters } = useGetAllRecruitersOfCompany(
		companyId || selectedCompany?.company?._id || id || user?.registeredCompany?.toString() || '',
	);

	useEffect(() => {
		console.log('useEffectcompanyId:', companyId, id);
		setNewRecruiterData({
			name: '',
			email: '',
			phone: '',
			companyId: companyId || id || user?.registeredCompany?.toString() || '',
			positionId: positionId || '',
			hideRecommendations: false,
			recruiterDesignation: '',
		});
	}, [id, companyId, user?.registeredCompany, positionId]);

	const inviteCollaboratorForPositionFn = useMutation(
		['inviteCollaboratorForPosition'],
		inviteCollaboratorForPosition,
		{
			onSuccess(data) {
				refetchCompanyRecruiters();
				setDisableNewRecruiterFields(false);
				queryClient.invalidateQueries(['getMyRegisteredPositions', selectedCompany?.company?._id || companyId]);
				setNewRecruiterData({
					name: '',
					email: '',
					phone: '',
					companyId: companyId || id || user?.registeredCompany?.toString() || '',
					positionId: positionId || '',
					hideRecommendations: false,
					recruiterDesignation: '',
				});
				setExistingRec(undefined);
				toast({
					title: 'Recruiter added successfully',
					duration: 5000,
					type: 'success',
				});
				onClose && onClose();
			},
			onError(error: Error) {
				const errorMessage = get(error, ['response', 'data', 'message'], error.message);
				toast({
					title: errorMessage,
					duration: 5000,
					type: 'error',
				});
			},
		},
	);

	return (
		<Drawer
			isOpen={isOpen}
			minHeight="fit-content"
			isModal={!isMobile}
			onClose={() => {
				onClose && onClose();
			}}
			header={<h1 className="px-4 pt-3 text-xl font-inter font-semibold">Provide details</h1>}
			body={
				<form
					id="inviteCollaborator"
					onSubmit={e => {
						e.preventDefault();
						inviteCollaboratorForPositionFn.mutate(newRecruiterData);
					}}
				>
					<div className="p-2 mt-2 flex flex-col gap-2">
						{/* Select HR Email */}
						<div>
							<div className="mb-2">
								<div className="block">
									<Label htmlFor="title" value="HR Email" className={`${labelStyle}`} />
									<span className="text-red-600 pl-1">*</span>
								</div>

								{newRecruiterData?.email && !validateEmailAddress(newRecruiterData?.email) ? (
									<span className="text-red-600 text-sm">Please enter a valid email address</span>
								) : null}
							</div>

							<HRSelection
								value={newRecruiterData?.email}
								recruitersData={recruitersData}
								textInputStyle={textInputStyle}
								companyId={
									companyId || selectedCompany?.company?._id || id || user?.registeredCompany?.toString() || ''
								}
								onChange={(email: string) => {
									const existingRec = recruitersData?.recruiters?.find(
										r => r.email.toLowerCase().trim() === email.toLowerCase().trim(),
									);
									setExistingRec(existingRec);
									if (existingRec) {
										setDisableNewRecruiterFields(true);
									} else {
										setDisableNewRecruiterFields(false);
									}
									setNewRecruiterData({
										email: email.toLowerCase().trim(),
										name: existingRec?.name || '',
										phone: existingRec?.phone || '',
										companyId:
											selectedCompany?.company?._id || companyId || id || user?.registeredCompany?.toString() || '',
										hideRecommendations: existingRec?.flags?.hideRecommendations,
										positionId: positionId,
										recruiterDesignation: existingRec?.recruiterDesignation,
									});
								}}
							/>
						</div>

						{/* HR Name */}
						<div>
							<div className="mb-2 block">
								<Label htmlFor="techStack" value="HR Name" className={`${labelStyle}`} />
								<span className="text-red-600 pl-1">*</span>

								{/* <div>
									{newRecruiterData?.name ? (
										<span className="text-red-600 text-sm">Please enter a valid name</span>
									) : null}
								</div> */}
							</div>
							<TextInput
								disabled={disableNewRecruiterFields && existingRec?.name ? true : false}
								placeholder="Suman"
								className="[&_input]:border [&_input]:!border-[#D1D5DB] [&_input]:bg-white  dark:[&_input]:bg-[#374151]"
								type="text"
								color="purplePlaced"
								value={newRecruiterData?.name}
								onChange={e => setNewRecruiterData({ ...newRecruiterData, name: e?.target?.value || '' })}
							/>
						</div>

						{/* HR Phone */}
						<div>
							<div className="mb-2 block">
								<Label htmlFor="techStack" value="HR Phone" className={`${labelStyle}`} />
								<span className="text-red-600 pl-1">*</span>

								<div>
									{newRecruiterData?.phone &&
									(!validatePhoneNumber(newRecruiterData?.phone) || !(newRecruiterData?.phone?.length === 10)) ? (
										<span className="text-red-600 text-sm">Please enter a valid phone number</span>
									) : null}
								</div>
							</div>
							<TextInput
								disabled={disableNewRecruiterFields && existingRec?.phone ? true : false}
								placeholder="9876xxxxxx"
								className="[&_input]:border [&_input]:!border-[#D1D5DB] [&_input]:bg-white  dark:[&_input]:bg-[#374151]"
								type="number"
								color="purplePlaced"
								value={newRecruiterData?.phone}
								onChange={e => setNewRecruiterData({ ...newRecruiterData, phone: e.target.value || '' })}
							/>
						</div>

						<div>
							<div className="mb-2 block">
								<Label htmlFor="techStack" value="Designation" className={`${labelStyle}`} />
							</div>
							<TextInput
								placeholder="HR, CEO"
								className="[&_input]:border [&_input]:!border-[#D1D5DB] [&_input]:bg-white  dark:[&_input]:bg-[#374151]"
								color="purplePlaced"
								value={newRecruiterData?.recruiterDesignation}
								onChange={e => setNewRecruiterData({ ...newRecruiterData, recruiterDesignation: e.target.value || '' })}
							/>
						</div>

						{isAccountManagerLoggedIn ? (
							<div>
								<Label htmlFor="hideRecommendations" value="Hide Recommendations" className={`${labelStyle}`} />

								<div className="min-h-[38px] flex items-center">
									<ToggleSwitch
										id="hideRecommendations"
										theme={{
											toggle: {
												checked: {
													color: {
														blue: 'bg-[#7777E7] border-[#7777E7]',
													},
												},
											},
										}}
										checked={newRecruiterData?.hideRecommendations || false}
										onChange={() => {
											setNewRecruiterData({
												...newRecruiterData,
												hideRecommendations: !newRecruiterData?.hideRecommendations,
											});
										}}
									/>
								</div>
							</div>
						) : null}
					</div>
				</form>
			}
			footer={
				<div className="dark:bg-[#111E2C] sticky bottom-0 py-3 px-[16px] rounded-b-md">
					<Button
						type="submit"
						form="inviteCollaborator"
						color="purplePlaced"
						className="w-full"
						disabled={
							!newRecruiterData?.email ||
							!newRecruiterData?.phone ||
							!validateEmailAddress(newRecruiterData?.email) ||
							!validatePhoneNumber(newRecruiterData?.phone) ||
							!(newRecruiterData?.phone?.length === 10) ||
							!newRecruiterData?.name ||
							inviteCollaboratorForPositionFn.isLoading
						}
					>
						{inviteCollaboratorForPositionFn.isLoading ? <Spinner /> : 'Invite'}
					</Button>
				</div>
			}
		/>
	);
};

export default InviteCollaborator;
